'use client';

import { useState } from 'react';
import { Map, Stack, Text, TextInput, Box, Button } from '@instamenu/ui-system-next';
import { useForm } from 'react-hook-form';
import { useCheckoutContext } from '@instamenu/context-checkout';
import { Address } from '@instamenu/context-user';
import { AddressAutocomplete } from '../places/places-autocomplete';

type FormData = {
  street2: string;
  specialInstructions: string;
};

type AddressFormProps = {
  onClose: () => void;
  toEdit?: Address;
  onSubmitAddress?: (address: Address, options: { isEdit: boolean }) => void; // used when there is no checkout context
};

export const AddressForm = (props: AddressFormProps) => {
  const checkout = useCheckoutContext();
  const { onClose, onSubmitAddress } = props;
  const [foundAddress, setFoundAddress] = useState<Address>();
  const {
    formState: { errors },
    register,
    handleSubmit,
    setError,
  } = useForm<FormData>({
    defaultValues: {
      street2: props.toEdit?.json.street2 || '',
      specialInstructions: props.toEdit?.json.specialInstructions || '',
    },
  });

  const submit = handleSubmit((data) => {
    if (!foundAddress && !props.toEdit) {
      return setError('root', { message: "That's not an address our drivers can deliver to." });
    }
    const addressToSave = props.toEdit || foundAddress;
    if (!addressToSave) return;

    addressToSave.updateFromJson(data);
    const isEdit = !!props.toEdit;

    if (onSubmitAddress) {
      onSubmitAddress(addressToSave, { isEdit });
    } else if (checkout) {
      if (isEdit) {
        checkout.addresses.setAddress(addressToSave);
        if (checkout.user && addressToSave.id) {
          checkout.user.update({
            deliveryAddressesAttributes: [addressToSave.asDeliveryAddressAttributes()],
          });
        }
      } else {
        checkout.addresses.add(addressToSave);
      }
    }
    onClose();
  });

  if (!checkout && !onSubmitAddress) return null;
  const location =
    foundAddress?.location ?? props.toEdit?.location ?? checkout?.restaurant?.address?.location;

  return (
    <Box css={{ padding: 16 }} data-testid="address-form">
      <Text variant="h2" css={{ marginBottom: 12 }}>
        Your delivery address
      </Text>
      <Stack style={{ maxHeight: '55dvh', overflowY: 'auto', gap: 4 }}>
        <AddressAutocomplete
          onChange={(address) => {
            if (address) setFoundAddress(address);
          }}
          initialFormattedAddress={
            foundAddress?.formattedAddress ?? props.toEdit?.formattedAddress ?? undefined
          }
          isLocationInputDisabled={!!props.toEdit}
        />
        <Map lat={location?.lat} lon={location?.lon} style={{ marginTop: 8, height: 200 }} />
        <Text variant="caption1" css={{ marginTop: 16 }}>
          Unit or apartment number
        </Text>
        <TextInput placeholder="e.g. Unit 123" {...register('street2')} />
        <Text variant="caption1" css={{ marginTop: 16 }}>
          Special instructions
        </Text>
        <TextInput placeholder="e.g. Leave with doorman" {...register('specialInstructions')} />
        {errors && (
          <Box css={{ marginTop: 16 }}>
            {Object.entries(errors).map(([key, error]) => (
              <Text key={key} error>
                {error.message}
              </Text>
            ))}
          </Box>
        )}
      </Stack>
      <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={() => props.onClose()} variant="secondary">
          Cancel
        </Button>
        <Button onClick={submit}>Save</Button>
      </Box>
    </Box>
  );
};
